import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Home, 
  ArrowLeft, 
  MessageSquare, 
  FileText,
  Phone,
  Mail,
  Calendar,
  Package,
  DollarSign,
  CheckCircle2,
  Circle,
  Edit,
  Trash2,
  Plus,
  Sparkles,
  Copy,
  Loader2,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SalesAgreementModal } from '@/components/SalesAgreementModal';
import { QuickEnquiryModal } from '@/components/QuickEnquiryModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// 8 Lifecycle Stages (SPEC NAMES)
const LIFECYCLE_STAGES = [
  { id: 1, key: 'application_submitted', label: 'Application Submitted', color: 'bg-gray-200' },
  { id: 2, key: 'under_vetting', label: 'Under Vetting', color: 'bg-yellow-200' },
  { id: 3, key: 'awaiting_dropoff', label: 'Awaiting Drop-off', color: 'bg-blue-200' },
  { id: 4, key: 'agreement_sent', label: 'Agreement Signed', color: 'bg-indigo-200' },
  { id: 5, key: 'awaiting_checkin', label: 'Awaiting Check-in', color: 'bg-purple-200' },
  { id: 6, key: 'in_preparation', label: 'In Preparation', color: 'bg-purple-200' },
  { id: 7, key: 'live_on_marketplace', label: 'Live on Marketplace', color: 'bg-green-200' },
  { id: 8, key: 'sold', label: 'Sold', color: 'bg-teal-200' }
];

export default function ItemDetail() {
  const { appId, itemId } = useParams();
  const navigate = useNavigate();
  const id = itemId || appId;
  const isItem = Boolean(itemId);

  const [data, setData] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Stage change modal
  const [stageModalOpen, setStageModalOpen] = useState(false);
  const [targetStage, setTargetStage] = useState(null);

  // AI listing generation
  const [generatingListing, setGeneratingListing] = useState(false);

  // Marketplace ID
  const [marketplaceId, setMarketplaceId] = useState('');
  const [savingMarketplaceId, setSavingMarketplaceId] = useState(false);

  // Signed sales agreement
  const [signedAgreement, setSignedAgreement] = useState(null);
  const [sendingAgreement, setSendingAgreement] = useState(null);

  // Inline price editing
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  // AI price estimate
  const [estimating, setEstimating] = useState(false);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [marketIntel, setMarketIntel] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [vettingResult, setVettingResult] = useState(null);
  const [vettingLoading, setVettingLoading] = useState(false);

  // Buyer CRM
  const [enquiryStats, setEnquiryStats] = useState(null);
  const [enquiryModalOpen, setEnquiryModalOpen] = useState(false);
  const [notifying, setNotifying] = useState(false);

  // Sold form state (final sale price + buyer name)
  const [saleForm, setSaleForm] = useState({ sale_price: '', buyer_name: '' });

  // Quick actions
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({ type: 'dropoff', title: '', start_time: '', end_time: '', location: '', notes: '' });
  const [savingBooking, setSavingBooking] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dropoffModalOpen, setDropoffModalOpen] = useState(false);
  const [dropoffForm, setDropoffForm] = useState({ start_time: '', location: 'Hope Valley storage facility', notes: '' });
  const [schedulingDropoff, setSchedulingDropoff] = useState(false);
  const [agreementModalOpen, setAgreementModalOpen] = useState(false);
  const [checkinModalOpen, setCheckinModalOpen] = useState(false);
  const [checkinStorage, setCheckinStorage] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);

  // Tab panel data
  const [notes, setNotes] = useState([]);
  const [tabTasks, setTabTasks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [enquiries, setEnquiries] = useState([]);

  const generateListing = async () => {
    try {
      setGeneratingListing(true);
      const endpoint = isItem
        ? `${API}/items/${id}/generate-listing`
        : `${API}/applications/${id}/generate-listing`;
      await axios.post(endpoint, {}, { withCredentials: true });
      toast.success('Marketplace listing generated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate listing');
    } finally {
      setGeneratingListing(false);
    }
  };

  useEffect(() => {
    setMarketplaceId(data?.marketplace_id || '');
  }, [data?.marketplace_id]);

  const agreementAppId = isItem ? data?.application_id : id;

  const loadEnquiryData = useCallback(async () => {
    if (!isItem) return;
    try {
      const [enqRes, statsRes] = await Promise.all([
        axios.get(`${API}/buyers/enquiries/item/${id}`, { withCredentials: true }),
        axios.get(`${API}/buyers/enquiries/item/${id}/stats`, { withCredentials: true }),
      ]);
      setEnquiries(enqRes.data || []);
      setEnquiryStats(statsRes.data);
    } catch (e) {
      // Enquiry stats are only available for items (not applications);
      // a 404 here is expected — log at debug for visibility only.
      console.debug('Enquiry data unavailable:', e?.response?.status);
    }
  }, [isItem, id]);

  useEffect(() => { loadEnquiryData(); }, [loadEnquiryData]);

  const notifyInterested = async () => {
    if (!window.confirm(`Send an SMS about this item to all ${enquiryStats?.interested_buyers} interested buyers?`)) return;
    try {
      setNotifying(true);
      const { data: res } = await axios.post(`${API}/buyers/enquiries/item/${id}/notify-interested`, {}, { withCredentials: true });
      toast.success(`Sent to ${res.sent} buyers${res.skipped ? ` (${res.skipped} skipped)` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to notify buyers');
    } finally {
      setNotifying(false);
    }
  };

  useEffect(() => {
    if (!agreementAppId) { setSignedAgreement(null); return; }
    axios.get(`${API}/applications/${agreementAppId}/agreement`, { withCredentials: true })
      .then(res => setSignedAgreement(res.data))
      .catch(() => setSignedAgreement(null));
  }, [agreementAppId]);

  const sendAgreement = async (via) => {
    try {
      setSendingAgreement(via);
      await axios.post(`${API}/applications/${agreementAppId}/agreement/send`, { via }, { withCredentials: true });
      toast.success(via === 'sms' ? 'Agreement link sent via SMS' : 'Agreement sent via email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send agreement');
    } finally {
      setSendingAgreement(null);
    }
  };

  const runVetting = async () => {
    try {
      setVettingLoading(true);
      const { data: v } = await axios.post(`${API}/ai/vet-application/${data.id}`, {}, { withCredentials: true });
      setVettingResult(v);
      toast.success(`AI vetting complete — scored ${v.score}/100`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI vetting failed');
    } finally {
      setVettingLoading(false);
    }
  };

  const getMarketIntel = async () => {
    try {
      setIntelLoading(true);
      const price = data.listed_price || data.nominated_sale_price;
      const params = new URLSearchParams({ category: data.category || '' });
      if (price) params.set('price', price);
      if (isItem) params.set('exclude_item_id', data.id);
      const { data: intel } = await axios.get(`${API}/ai/price-intelligence?${params}`, { withCredentials: true });
      setMarketIntel(intel);
      if (!intel.comparables?.length) toast.info('No sold items in this category yet — comparables will grow as you sell');
    } catch (e) {
      toast.error('Failed to load comparable sales');
    } finally {
      setIntelLoading(false);
    }
  };

  const getPriceEstimate = async () => {
    try {
      setEstimating(true);
      const { data: est } = await axios.post(`${API}/ai/price-estimate`, {
        title: data?.item_title || data?.title,
        category: data?.category,
        condition: data?.condition,
        description: data?.description,
        nominated_price: data?.nominated_sale_price
      }, { withCredentials: true });
      setPriceEstimate(est);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Price estimate failed');
    } finally {
      setEstimating(false);
    }
  };

  const applyEstimate = async () => {
    if (!priceEstimate) return;
    try {
      if (isItem) {
        await axios.patch(`${API}/items/${id}`, { listed_price: priceEstimate.suggested }, { withCredentials: true });
      } else {
        await axios.patch(`${API}/applications/${id}`, { nominated_sale_price: priceEstimate.suggested }, { withCredentials: true });
      }
      toast.success(`Price set to $${priceEstimate.suggested.toLocaleString()}`);
      setPriceEstimate(null);
      loadData();
    } catch (error) {
      toast.error('Failed to apply price');
    }
  };

  const savePrice = async () => {
    const value = parseFloat(priceInput);
    if (Number.isNaN(value) || value <= 0) {
      toast.error('Enter a valid price');
      return;
    }
    try {
      setSavingPrice(true);
      if (isItem) {
        await axios.patch(`${API}/items/${id}`, { listed_price: value }, { withCredentials: true });
      } else {
        await axios.patch(`${API}/applications/${id}`, { nominated_sale_price: value }, { withCredentials: true });
      }
      toast.success('Price updated');
      setEditingPrice(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update price');
    } finally {
      setSavingPrice(false);
    }
  };

  const saveMarketplaceId = async () => {
    try {
      setSavingMarketplaceId(true);
      await axios.patch(`${API}/items/${id}`, { marketplace_id: marketplaceId.trim() }, { withCredentials: true });
      toast.success('Marketplace ID saved');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Marketplace ID');
    } finally {
      setSavingMarketplaceId(false);
    }
  };

  const togglePrepCheck = async (key) => {
    try {
      const newValue = !data.preparation_checks[key];
      await axios.patch(`${API}/items/${id}/preparation`, { [key]: newValue }, { withCredentials: true });
      toast.success(`${key.replace(/_/g, ' ')} ${newValue ? 'completed' : 'unchecked'}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update preparation check');
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const BOOKING_TYPE_LABELS = { dropoff: 'Drop-off', inspection: 'Inspection', payout_reminder: 'Payout Reminder', internal_task: 'Internal Task', follow_up: 'Follow-up' };

  const openBookingModal = () => {
    setBookingForm({
      type: 'dropoff',
      title: `Drop-off — ${data?.item_title || data?.title || ''}`,
      start_time: '', end_time: '', location: '', notes: ''
    });
    setBookingModalOpen(true);
  };

  const handleBookingTypeChange = (type) => {
    setBookingForm(f => ({ ...f, type, title: `${BOOKING_TYPE_LABELS[type]} — ${data?.item_title || data?.title || ''}` }));
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    try {
      setSavingBooking(true);
      await axios.post(`${API}/bookings`, {
        type: bookingForm.type,
        title: bookingForm.title,
        start_time: new Date(bookingForm.start_time).toISOString(),
        end_time: new Date(bookingForm.end_time).toISOString(),
        location: bookingForm.location || null,
        notes: bookingForm.notes || null,
        seller_id: data?.seller_id || null,
        item_id: isItem ? id : null,
        application_id: isItem ? null : id
      }, { withCredentials: true });
      toast.success('Booking scheduled');
      setBookingModalOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to schedule booking');
    } finally {
      setSavingBooking(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const endpoint = isItem ? `${API}/items/${id}` : `${API}/applications/${id}`;
      await axios.delete(endpoint, { withCredentials: true });
      toast.success(`${isItem ? 'Item' : 'Application'} deleted`);
      navigate(isItem ? '/items' : '/applications-list');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete');
      setDeleting(false);
    }
  };


  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    try {
      setSavingNote(true);
      const endpoint = isItem ? `${API}/items/${id}/notes` : `${API}/applications/${id}/notes`;
      await axios.post(endpoint, { note: noteText.trim() }, { withCredentials: true });
      toast.success('Note added');
      setNoteModalOpen(false);
      setNoteText('');
      loadTabData();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };


  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      let endpoint = isItem ? `${API}/items/${id}` : `${API}/applications/${id}`;
      const response = await axios.get(endpoint, { withCredentials: true });
      setData(response.data);
      
      // Load seller if not included
      if (response.data.seller_id && !response.data.seller) {
        const sellerRes = await axios.get(`${API}/sellers/${response.data.seller_id}`, { withCredentials: true });
        setSeller(sellerRes.data);
      } else {
        setSeller(response.data.seller);
      }
    } catch (error) {
      // Error handled via toast
      toast.error('Failed to load details');
    } finally {
      setLoading(false);
    }
  }, [id, isItem]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadTabData = useCallback(async () => {
    const base = isItem ? `${API}/items/${id}` : `${API}/applications/${id}`;
    const cfg = { withCredentials: true };
    const results = await Promise.allSettled([
      axios.get(`${base}/notes`, cfg),
      axios.get(`${API}/tasks?${isItem ? 'item_id' : 'application_id'}=${id}`, cfg),
      axios.get(`${base}/activity`, cfg),
      isItem ? axios.get(`${API}/buyers/enquiries/item/${id}`, cfg) : Promise.resolve({ data: [] })
    ]);
    if (results[0].status === 'fulfilled') setNotes(results[0].value.data || []);
    if (results[1].status === 'fulfilled') setTabTasks(results[1].value.data || []);
    if (results[2].status === 'fulfilled') setActivity(results[2].value.data || []);
    if (results[3].status === 'fulfilled') setEnquiries(results[3].value.data || []);
  }, [id, isItem]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  const completeTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/complete`, {}, { withCredentials: true });
      toast.success('Task completed');
      loadTabData();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleStageChange = async (newStage) => {
    // Stage 3 for applications: schedule the drop-off booking first
    if (!isItem && newStage.key === 'awaiting_dropoff') {
      setDropoffForm({ start_time: '', location: 'Hope Valley storage facility', notes: '' });
      setDropoffModalOpen(true);
      return;
    }
    // Check-in for applications: assign storage space + generate QR label
    if (!isItem && newStage.key === 'in_preparation') {
      setCheckinStorage('');
      setCheckinModalOpen(true);
      return;
    }
    setTargetStage(newStage);
    setStageModalOpen(true);
  };

  const confirmDropoffSchedule = async (e) => {
    e.preventDefault();
    if (!dropoffForm.start_time) {
      toast.error('Please pick a drop-off date and time');
      return;
    }
    try {
      setSchedulingDropoff(true);
      const startISO = new Date(dropoffForm.start_time).toISOString();
      const endISO = new Date(new Date(dropoffForm.start_time).getTime() + 30 * 60000).toISOString();
      await axios.post(`${API}/bookings`, {
        type: 'dropoff',
        title: `Drop-off — ${data?.item_title || 'Item'}`,
        start_time: startISO,
        end_time: endISO,
        location: dropoffForm.location || null,
        notes: dropoffForm.notes || null,
        seller_id: data?.seller_id || null,
        application_id: id
      }, { withCredentials: true });
      await axios.patch(`${API}/applications/${id}`, {
        lifecycle_stage: 'awaiting_dropoff',
        dropoff_datetime: startISO,
        dropoff_location: dropoffForm.location || null
      }, { withCredentials: true });
      toast.success('Drop-off booked in calendar — confirmation SMS sent to seller');
      setDropoffModalOpen(false);
      loadData();
      loadTabData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to schedule drop-off');
    } finally {
      setSchedulingDropoff(false);
    }
  };

  const confirmCheckin = async (e) => {
    e.preventDefault();
    if (!checkinStorage.trim()) {
      toast.error('Please assign a storage space (e.g., Bay A3)');
      return;
    }
    try {
      setCheckingIn(true);
      const res = await axios.post(`${API}/applications/${id}/convert-to-item`, {
        storage_location: checkinStorage.trim()
      }, { withCredentials: true });
      toast.success(`Item checked in — QR label generated, assigned to ${checkinStorage.trim()}`);
      setCheckinModalOpen(false);
      navigate(`/item/${res.data.id}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to check in item');
    } finally {
      setCheckingIn(false);
    }
  };

  const printLabel = () => {
    const w = window.open('', '_blank', 'width=450,height=600');
    if (!w) { toast.error('Pop-up blocked — allow pop-ups to print labels'); return; }
    w.document.title = 'Listers Item Label';
    const body = w.document.body;
    body.style.cssText = 'font-family:Arial,sans-serif;text-align:center;padding:16px';

    // Build the label with safe DOM APIs (no innerHTML → no XSS surface even
    // if data.title / reference_code ever contain user-provided characters).
    const mk = (tag, style, text) => {
      const el = w.document.createElement(tag);
      if (style) el.style.cssText = style;
      if (text != null) el.textContent = String(text);
      return el;
    };
    body.appendChild(mk('h2', 'margin:4px 0;letter-spacing:2px', 'LISTERS'));
    const img = w.document.createElement('img');
    img.style.cssText = 'width:220px;height:220px';
    img.alt = 'QR code';
    if (data?.qr_code) img.src = data.qr_code;
    body.appendChild(img);
    body.appendChild(mk('p', 'font-size:18px;font-weight:bold;margin:6px 0', data?.reference_code || ''));
    body.appendChild(mk('p', 'font-size:14px;margin:4px 0', data?.title || data?.item_title || ''));
    body.appendChild(mk(
      'p',
      'font-size:16px;font-weight:bold;margin:6px 0;border:2px solid #000;display:inline-block;padding:4px 14px',
      `Storage: ${data?.storage_location || '—'}`
    ));

    const doPrint = () => { w.focus(); w.print(); };
    if (!img.complete) { img.onload = doPrint; img.onerror = doPrint; } else { doPrint(); }
  };

  const confirmStageChange = async () => {
    try {
      // CASE A: viewing an application (unconverted). We convert it into an
      // item first, then optionally push it straight to the target stage.
      if (!isItem) {
        // If the application is already converted, just navigate to the item —
        // no need to re-convert.
        if (data?.converted_to_item && data?.item_id) {
          toast.message('This application is already an item — jumping to it');
          setStageModalOpen(false);
          navigate(`/item/${data.item_id}`);
          return;
        }

        // Selling straight from an application is not supported without a price
        // (payout depends on it). Force the user through the sold flow on the
        // item after we convert.
        const wantsSold = targetStage.key === 'sold';
        if (wantsSold) {
          toast.error('Move the item to a later stage first, then mark it sold from the item page (a sale price is required).');
          return;
        }

        // Convert first (creates the item record + QR code)
        const res = await axios.post(`${API}/applications/${id}/convert-to-item`, {}, { withCredentials: true });
        const newItemId = res.data.id;

        // If the user picked "In Preparation" this is a normal check-in.
        if (targetStage.key === 'in_preparation') {
          toast.success('Item checked in — application converted to an active item');
          setStageModalOpen(false);
          navigate(`/item/${newItemId}`);
          return;
        }

        // Skipping ahead (e.g. straight to Live on Marketplace) — apply the
        // target stage on the freshly created item and warn the user.
        await axios.patch(`${API}/items/${newItemId}`, {
          lifecycle_stage: targetStage.key
        }, { withCredentials: true });
        toast.warning(`Skipped check-in — item created and moved directly to ${targetStage.label}. Set storage + QR label on the item page when ready.`);
        setStageModalOpen(false);
        navigate(`/item/${newItemId}`);
        return;
      }

      // CASE B: viewing an item.
      // Selling still requires a captured sale price + payout.
      if (targetStage.key === 'sold') {
        const price = parseFloat(saleForm.sale_price);
        if (!price || price <= 0) {
          toast.error('Enter the final sale price to mark this item sold');
          return;
        }
        await axios.post(`${API}/items/${id}/mark-sold`, {
          sale_price: price,
          buyer_name: saleForm.buyer_name || null
        }, { withCredentials: true });
        toast.success(`Sold for $${price.toFixed(2)} — payout created ($${(price * 0.7).toFixed(2)} seller / $${(price * 0.3).toFixed(2)} Listers)`);
        setStageModalOpen(false);
        setSaleForm({ sale_price: '', buyer_name: '' });
        loadData();
        return;
      }

      // Any other item stage change — allow freely, but if it's a jump (skips
      // stages) warn the operator so they know something might be incomplete.
      const currentIdx = LIFECYCLE_STAGES.findIndex(s => s.key === data?.lifecycle_stage);
      const targetIdx = LIFECYCLE_STAGES.findIndex(s => s.key === targetStage.key);
      const skipping = currentIdx >= 0 && targetIdx >= 0 && Math.abs(targetIdx - currentIdx) > 1;

      await axios.patch(`${API}/items/${id}`, {
        lifecycle_stage: targetStage.key
      }, { withCredentials: true });

      if (skipping) {
        toast.warning(`Stage jumped to ${targetStage.label} — some in-between checks were skipped.`);
      } else {
        toast.success(`Stage updated to: ${targetStage.label}`);
      }
      setStageModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const getCurrentStageIndex = () => {
    if (!data) return -1;
    if (data.lifecycle_stage === 'payout_issued') return LIFECYCLE_STAGES.length;
    return LIFECYCLE_STAGES.findIndex(s => s.key === data.lifecycle_stage);
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <p className="text-[#474747]">Loading...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <p className="text-[#474747]">Not found</p>
      </div>
    );
  }

  const currentStageIndex = getCurrentStageIndex();
  const title = data.title || data.item_title || 'Untitled';
  const referenceCode = data.reference_code || 'N/A';

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E9E9E9]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#474747]"
            >
              <Home className="w-4 h-4" />
              Back to Dashboard
            </button>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/messages', { state: { compose: { type: 'sms', to: seller?.phone || '' } } })}
                variant="outline"
                className="border-[#E9E9E9]"
                data-testid="header-sms-seller-button"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                SMS Seller
              </Button>
              
              <Button
                onClick={() => navigate(`/seller/${data.seller_id}`)}
                className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
              >
                View Seller Profile
              </Button>

              <Button
                onClick={() => setDeleteModalOpen(true)}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid="delete-record-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-heading font-semibold text-[#474747]">{title}</h1>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                  {referenceCode}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                {seller?.full_name} • {seller?.phone} • Submitted {formatDate(data.submitted_at || data.created_at)}
              </p>
            </div>

            <Button
              onClick={() => {/* Open actions menu */}}
              variant="outline"
              className="border-[#E9E9E9]"
            >
              Actions
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Horizontal Lifecycle Tracker */}
        <div className="bg-white rounded-xl border border-[#E9E9E9] p-8 mb-6">
          <h2 className="text-xl font-semibold text-[#474747] mb-6">Lifecycle Stage</h2>
          
          <div className="flex items-center justify-between">
            {LIFECYCLE_STAGES.map((stage, index) => {
              const isCompleted = index < currentStageIndex;
              const isCurrent = index === currentStageIndex;
              const isFuture = index > currentStageIndex;
              
              return (
                <React.Fragment key={stage.id}>
                  {/* Stage Circle */}
                  <div className="flex flex-col items-center flex-1">
                    <button
                      onClick={() => handleStageChange(stage)}
                      data-testid={`stage-button-${stage.key}`}
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all ${
                        isCompleted ? 'bg-[#C6E76C] text-[#474747]' :
                        isCurrent ? 'bg-[#C6E76C] text-[#474747] ring-4 ring-[#C6E76C]/30 animate-pulse' :
                        'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {isCompleted ? <CheckCircle2 className="w-6 h-6" /> : stage.id}
                    </button>
                    <p className={`text-xs mt-2 text-center font-medium ${
                      isCurrent ? 'text-[#474747]' : 'text-gray-500'
                    }`}>
                      {stage.label}
                    </p>
                  </div>
                  
                  {/* Connector Line */}
                  {index < LIFECYCLE_STAGES.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      index < currentStageIndex ? 'bg-[#C6E76C]' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Current Stage:</span> {LIFECYCLE_STAGES[currentStageIndex]?.label || (data.lifecycle_stage === 'payout_issued' ? 'Payout Issued — Lifecycle Complete' : data.lifecycle_stage || 'Unknown')}
              {data.dropoff_confirmed && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C6E76C] text-[#1E2117] text-xs font-bold" data-testid="dropoff-confirmed-badge">
                  ✓ Drop-off confirmed by seller
                </span>
              )}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Click on a stage to move the item through the lifecycle
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview', 'photos', 'listing', 'buyers', 'activity', 'notes', 'tasks'].map(tab => (
            <button
              key={tab}
              data-testid={`detail-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-[#C6E76C] text-[#474747]'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {activeTab === 'overview' && (
              <>
                {/* Item Details */}
                <div className="bg-white rounded-xl border border-[#E9E9E9] p-6">
                  <h3 className="text-lg font-semibold text-[#474747] mb-4">Item Details</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Category</p>
                      <p className="text-base text-[#474747] mt-1">{data.category || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Condition</p>
                      <p className="text-base text-[#474747] mt-1">{data.condition || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">{isItem ? 'Listed Price' : 'Nominated Price'}</p>
                      {editingPrice ? (
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={priceInput}
                            onChange={(e) => setPriceInput(e.target.value)}
                            className="h-9 w-32 text-sm"
                            data-testid="price-input"
                          />
                          <Button
                            size="sm"
                            onClick={savePrice}
                            disabled={savingPrice}
                            className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
                            data-testid="save-price-button"
                          >
                            {savingPrice ? '…' : 'Save'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingPrice(false)} data-testid="cancel-price-button">
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 mt-1">
                          <p className="text-base font-semibold text-[#474747]" data-testid="detail-price-value">
                            {formatCurrency(isItem ? (data.listed_price ?? data.nominated_sale_price) : data.nominated_sale_price)}
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2.5 text-xs border-[#C6E76C] text-[#474747] hover:bg-[#F7FBEC]"
                            onClick={() => {
                              setPriceInput(String((isItem ? data.listed_price : data.nominated_sale_price) ?? data.nominated_sale_price ?? ''));
                              setEditingPrice(true);
                            }}
                            data-testid="edit-price-button"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit Price
                          </Button>
                        </div>
                      )}
                    </div>
                    {isItem && (
                      <div>
                        <p className="text-sm text-gray-500 font-medium">Nominated Price</p>
                        <p className="text-base text-[#474747] mt-1">{formatCurrency(data.nominated_sale_price)}</p>
                      </div>
                    )}
                  </div>

                  {/* AI Price Estimate */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    {!priceEstimate ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={getPriceEstimate}
                        disabled={estimating}
                        className="border-[#C6E76C] text-[#474747] hover:bg-[#F7FBEC]"
                        data-testid="ai-price-estimate-button"
                      >
                        <Sparkles className="w-4 h-4 mr-2 text-[#8CA43C]" />
                        {estimating ? 'Analysing Perth market…' : 'AI Price Estimate'}
                      </Button>
                    ) : (
                      <div className="bg-[#F7FBEC] border border-[#C6E76C] rounded-lg p-4" data-testid="price-estimate-panel">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-[#8CA43C]" />
                          <p className="text-sm font-bold text-[#1E2117]">AI Price Estimate — Perth resale market</p>
                        </div>
                        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mb-2">
                          <p className="text-sm text-gray-600">Range: <span className="font-bold text-[#1E2117]">${priceEstimate.low.toLocaleString()} – ${priceEstimate.high.toLocaleString()}</span></p>
                          <p className="text-sm text-gray-600">Suggested: <span className="font-bold text-[#1E2117] text-lg" data-testid="suggested-price">${priceEstimate.suggested.toLocaleString()}</span></p>
                        </div>
                        <p className="text-xs text-gray-600 mb-1">{priceEstimate.reasoning}</p>
                        <p className="text-[10px] text-gray-400 mb-3">{priceEstimate.disclaimer}</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={applyEstimate}
                            className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
                            data-testid="apply-estimate-button"
                          >
                            Apply ${priceEstimate.suggested.toLocaleString()} as {isItem ? 'Listed' : 'Nominated'} Price
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setPriceEstimate(null)} data-testid="dismiss-estimate-button">
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Comparable Sales (Price Intelligence) */}
                  <div className="mt-3">
                    {!marketIntel ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={getMarketIntel}
                        disabled={intelLoading}
                        className="border-[#C6E76C] text-[#474747] hover:bg-[#F7FBEC]"
                        data-testid="comparable-sales-button"
                      >
                        <TrendingUp className="w-4 h-4 mr-2 text-[#8CA43C]" />
                        {intelLoading ? 'Checking sold history…' : 'Comparable Sales'}
                      </Button>
                    ) : marketIntel.comparables?.length > 0 ? (
                      <div className="bg-white border border-[#E9E9E9] rounded-lg p-4" data-testid="comparable-sales-panel">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-[#8CA43C]" />
                            <p className="text-sm font-bold text-[#1E2117]">Comparable Sales — {marketIntel.category}</p>
                          </div>
                          <button onClick={() => setMarketIntel(null)} className="text-xs text-gray-400 hover:text-gray-600" data-testid="dismiss-intel-button">Close</button>
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 mb-3 text-sm text-gray-600">
                          <span>{marketIntel.stats.count} sold</span>
                          <span>Avg sale: <span className="font-bold text-[#1E2117]">${Number(marketIntel.stats.avg_sale_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></span>
                          <span>Range: ${Number(marketIntel.stats.min_sale_price).toLocaleString()} – ${Number(marketIntel.stats.max_sale_price).toLocaleString()}</span>
                          {marketIntel.stats.avg_days_to_sell != null && <span>Avg time to sell: <span className="font-semibold">{marketIntel.stats.avg_days_to_sell} days</span></span>}
                          {marketIntel.stats.avg_pct_of_listed != null && <span>Avg % of listed achieved: <span className="font-semibold">{marketIntel.stats.avg_pct_of_listed}%</span></span>}
                        </div>
                        <div className="divide-y divide-gray-100">
                          {marketIntel.comparables.map(c => (
                            <div key={c.id} className="flex items-center justify-between py-1.5 text-sm" data-testid={`comparable-${c.id}`}>
                              <span className="text-[#474747] truncate mr-3">{c.title}</span>
                              <span className="flex items-center gap-3 shrink-0 text-xs text-gray-500">
                                {c.days_to_sell != null && <span>{c.days_to_sell}d to sell</span>}
                                <span className="font-bold text-sm text-[#1E2117]">${Number(c.sale_price).toLocaleString()}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400" data-testid="no-comparables-note">No sold items in this category yet — comparable sales will appear as your history grows.</p>
                    )}
                  </div>

                  {/* AI Vetting (applications only) */}
                  {!isItem && (() => {
                    const vetting = vettingResult || data.ai_vetting;
                    const recStyles = {
                      approve: 'bg-green-100 text-green-700',
                      review: 'bg-amber-100 text-amber-700',
                      decline: 'bg-red-100 text-red-700',
                    };
                    return (
                      <div className="mt-3">
                        {!vetting ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={runVetting}
                            disabled={vettingLoading}
                            className="border-[#C6E76C] text-[#474747] hover:bg-[#F7FBEC]"
                            data-testid="ai-vetting-button"
                          >
                            <Sparkles className="w-4 h-4 mr-2 text-[#8CA43C]" />
                            {vettingLoading ? 'Vetting application…' : 'AI Vetting Score'}
                          </Button>
                        ) : (
                          <div className="bg-white border border-[#E9E9E9] rounded-lg p-4" data-testid="ai-vetting-panel">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-[#8CA43C]" />
                                <p className="text-sm font-bold text-[#1E2117]">AI Vetting</p>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${recStyles[vetting.recommendation] || recStyles.review}`} data-testid="vetting-recommendation">
                                  {vetting.recommendation}
                                </span>
                                <span className="text-lg font-black text-[#1E2117]" data-testid="vetting-score">{vetting.score}<span className="text-xs text-gray-400 font-normal">/100</span></span>
                              </div>
                              <Button size="sm" variant="outline" onClick={runVetting} disabled={vettingLoading} data-testid="rerun-vetting-button">
                                {vettingLoading ? 'Re-scoring…' : 'Re-run'}
                              </Button>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{vetting.summary}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {vetting.positives?.length > 0 && (
                                <div>
                                  <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1">Positives</p>
                                  <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                    {vetting.positives.map((p, i) => <li key={`pos-${i}-${(p || '').slice(0, 20)}`}>{p}</li>)}
                                  </ul>
                                </div>
                              )}
                              {vetting.concerns?.length > 0 && (
                                <div>
                                  <p className="text-xs font-bold text-red-600 uppercase tracking-wide mb-1">Concerns</p>
                                  <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                    {vetting.concerns.map((c, i) => <li key={`con-${i}-${(c || '').slice(0, 20)}`}>{c}</li>)}
                                  </ul>
                                </div>
                              )}
                            </div>
                            {vetting.vetted_at && <p className="text-[10px] text-gray-400 mt-2">Scored {new Date(vetting.vetted_at).toLocaleString('en-AU')} • AI assessment only — final call is always yours</p>}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {data.description && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 font-medium">Description</p>
                      <p className="text-base text-[#474747] mt-1 whitespace-pre-wrap">{data.description}</p>
                    </div>
                  )}
                </div>

                {/* Preparation Checklist (if item) */}
                {isItem && (
                  <div className="bg-white rounded-xl border border-[#E9E9E9] p-6">
                    <h3 className="text-lg font-semibold text-[#474747] mb-4">Preparation Checklist</h3>
                    
                    <div className="space-y-3">
                      {['item_received', 'cleaned', 'tested', 'photographed', 'ad_created'].map((key) => {
                        const value = !!data.preparation_checks?.[key];
                        return (
                        <button
                          type="button"
                          key={key}
                          onClick={() => togglePrepCheck(key)}
                          className="flex items-center gap-3 w-full text-left rounded-lg px-2 py-1.5 -mx-2 hover:bg-gray-50 transition-colors"
                          data-testid={`prep-check-${key}`}
                        >
                          <div className={`w-5 h-5 rounded flex items-center justify-center ${
                            value ? 'bg-[#C6E76C]' : 'bg-gray-200'
                          }`}>
                            {value && <CheckCircle2 className="w-4 h-4 text-[#474747]" />}
                          </div>
                          <span className="text-sm text-[#474747] capitalize">{key.replace('_', ' ')}</span>
                          {value && (
                            <span className="text-xs text-gray-500 ml-auto">Completed {formatDate(data.updated_at)}</span>
                          )}
                        </button>
                      );})}
                    </div>
                    <p className="text-xs text-gray-400 mt-3">Click a step to mark it complete.</p>
                  </div>
                )}

                {/* Storage Location (if item) */}
                {isItem && (
                  <div className="bg-white rounded-xl border border-[#E9E9E9] p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <h3 className="text-lg font-semibold text-[#474747]">Storage Location</h3>
                      <button
                        type="button"
                        onClick={() => {
                          const next = window.prompt('Update storage location', data.storage_location || '');
                          if (next !== null && next.trim() !== (data.storage_location || '')) {
                            axios.patch(`${API}/items/${id}`, { storage_location: next.trim() })
                              .then(() => { toast.success('Storage location updated'); fetchData(); })
                              .catch(() => toast.error('Failed to update storage location'));
                          }
                        }}
                        className="text-xs font-semibold text-listers-green hover:underline"
                        data-testid="edit-storage-location-btn"
                      >
                        Edit
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 font-medium">Storage Space</p>
                        <p className="text-base font-semibold text-[#474747] mt-1" data-testid="storage-location-value">
                          {data.storage_location || 'Not assigned'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 font-medium">Checked In</p>
                        <p className="text-base text-[#474747] mt-1">{formatDate(data.check_in_date) || '—'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'listing' && (
              <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="listing-tab-panel">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-semibold text-[#474747]">Marketplace Listing</h3>
                  <Button
                    size="sm"
                    onClick={generateListing}
                    disabled={generatingListing}
                    className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
                    data-testid="generate-listing-button"
                  >
                    {generatingListing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> {data.generated_listing ? 'Regenerate Listing' : 'Generate Marketplace Listing'}</>
                    )}
                  </Button>
                </div>

                {data.generated_listing ? (
                  <div className="space-y-5" data-testid="generated-listing">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Listing Title</p>
                        <button onClick={() => copyText(data.generated_listing.title, 'Title')} className="text-gray-400 hover:text-[#474747]" data-testid="copy-title-button">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-base font-semibold text-[#474747] bg-gray-50 rounded-lg px-4 py-3 border border-[#E9E9E9]" data-testid="listing-title">
                        {data.generated_listing.title}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Description</p>
                        <button onClick={() => copyText(data.generated_listing.description, 'Description')} className="text-gray-400 hover:text-[#474747]" data-testid="copy-description-button">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-[#474747] whitespace-pre-line bg-gray-50 rounded-lg px-4 py-3 border border-[#E9E9E9]" data-testid="listing-description">
                        {data.generated_listing.description}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Suggested Tags ({data.generated_listing.tags?.length || 0})</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyText((data.generated_listing.tags || []).join(', '), 'All tags')}
                          className="h-7 text-xs"
                          data-testid="copy-all-tags-button"
                        >
                          <Copy className="w-3 h-3 mr-1.5" /> Copy all tags
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(data.generated_listing.tags || []).map((tag) => (
                          <button
                            key={tag}
                            onClick={() => copyText(tag, `Tag "${tag}"`)}
                            className="px-3 py-1.5 rounded-full bg-[#F0F4E4] border border-[#E3E8D5] text-xs font-medium text-[#474747] hover:bg-[#C6E76C] transition-colors cursor-pointer"
                            data-testid={`listing-tag-${tag.replace(/\s+/g, '-')}`}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-2">Click any tag to copy it individually.</p>
                    </div>
                    {data.generated_listing.generated_at && (
                      <p className="text-xs text-gray-400">Generated {formatDate(data.generated_listing.generated_at)}</p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-10" data-testid="no-listing-placeholder">
                    <Sparkles className="w-8 h-8 text-[#C6E76C] mx-auto mb-3" />
                    <p className="text-sm text-gray-500 max-w-md mx-auto">
                      Generate the best, highest-performing marketplace listing for this item — an optimised title,
                      a high-converting description, and 20 suggested search tags you can copy with one click.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'photos' && (
              <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="photos-tab-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#474747]">
                    Photos {((data.photos?.length || 0) + (data.photo_urls?.length || 0) + (data.listers_photo_urls?.length || 0)) > 0 ? `(${(data.photos?.length || 0) + (data.photo_urls?.length || 0) + (data.listers_photo_urls?.length || 0)})` : ''}
                  </h3>
                </div>
                {((data.photos?.length || 0) + (data.photo_urls?.length || 0) + (data.listers_photo_urls?.length || 0)) > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {(data.photos || []).concat(data.photo_urls || []).concat(data.listers_photo_urls || []).map((photo, index) => (
                      <a key={`app-photo-${index}`} href={photo} target="_blank" rel="noreferrer" className="block group">
                        <img
                          src={photo}
                          alt={`${data.item_title || data.title || 'Item'} photo ${index + 1}`}
                          className="w-full h-40 object-cover rounded-lg border border-[#E9E9E9] group-hover:opacity-90 transition-opacity"
                          data-testid={`item-photo-${index}`}
                        />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500" data-testid="no-photos-message">No photos were uploaded with this application.</p>
                )}
              </div>
            )}

            {activeTab === 'buyers' && (
              <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="buyers-tab-panel">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <h3 className="text-lg font-semibold text-[#474747]">Buyer Enquiries {enquiries.length > 0 ? `(${enquiries.length})` : ''}</h3>
                  {isItem && (
                    <Button size="sm" onClick={() => setEnquiryModalOpen(true)} className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#1E2117] font-semibold" data-testid="item-log-enquiry-button">
                      <Plus className="w-4 h-4 mr-1" /> Log Enquiry
                    </Button>
                  )}
                </div>
                {isItem && enquiryStats && (
                  <>
                    <div className="flex flex-wrap gap-2 mb-4" data-testid="enquiry-stats-row">
                      <span className="px-3 py-1 rounded-full bg-gray-100 text-xs font-semibold text-gray-700">Enquiries: {enquiryStats.enquiries}</span>
                      <span className="px-3 py-1 rounded-full bg-gray-100 text-xs font-semibold text-gray-700">Offers: {enquiryStats.offers}</span>
                      <span className="px-3 py-1 rounded-full bg-gray-100 text-xs font-semibold text-gray-700">Inspections: {enquiryStats.inspections}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                        enquiryStats.demand === 'high' ? 'bg-green-100 text-green-700' :
                        enquiryStats.demand === 'medium' ? 'bg-amber-100 text-amber-700' :
                        enquiryStats.demand === 'low' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                      }`} data-testid="demand-pill">Demand: {enquiryStats.demand}</span>
                    </div>
                    {enquiryStats.interested_buyers > 0 && (
                      <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg bg-[#F7FBEC] border border-[#C6E76C] mb-4" data-testid="interested-buyers-banner">
                        <p className="text-sm text-[#3D4237]">
                          <span className="font-bold">{enquiryStats.interested_buyers} buyer{enquiryStats.interested_buyers === 1 ? ' has' : 's have'}</span> expressed interest in <span className="font-semibold">{enquiryStats.category}</span> items
                        </p>
                        <Button size="sm" onClick={notifyInterested} disabled={notifying} className="bg-[#1E2117] hover:bg-[#33382A] text-[#C6E76C] font-semibold" data-testid="notify-interested-button">
                          {notifying ? 'Sending…' : 'Notify All via SMS'}
                        </Button>
                      </div>
                    )}
                  </>
                )}
                {!isItem ? (
                  <p className="text-sm text-gray-500">Buyer enquiries become available once this application is checked in as an item.</p>
                ) : enquiries.length === 0 ? (
                  <p className="text-sm text-gray-500">No buyer enquiries yet for this item.</p>
                ) : (
                  <div className="divide-y divide-[#E9E9E9]">
                    {enquiries.map((enq) => (
                      <div key={enq.id} className="py-3">
                        <div className="flex items-center justify-between">
                          <button onClick={() => enq.buyer_id && navigate(`/buyer/${enq.buyer_id}`)} className="text-sm font-medium text-[#474747] hover:underline text-left">
                            {enq.buyer?.name || enq.buyer_name || 'Unknown buyer'}
                          </button>
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#F7FBEC] border border-[#C6E76C] text-[#3D4237] capitalize">{enq.type || 'enquiry'}{enq.offer_amount ? ` $${Number(enq.offer_amount).toLocaleString()}` : ''}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">{enq.status || 'new'}</span>
                          </div>
                        </div>
                        {(enq.buyer?.phone || enq.buyer_phone) && <p className="text-xs text-gray-500 mt-1">{enq.buyer?.phone || enq.buyer_phone}</p>}
                        {enq.message && <p className="text-sm text-gray-600 mt-1">{enq.message}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="activity-tab-panel">
                <h3 className="text-lg font-semibold text-[#474747] mb-4">Activity Timeline</h3>
                {activity.length === 0 ? (
                  <p className="text-sm text-gray-500">No activity recorded yet.</p>
                ) : (
                  <div className="space-y-4">
                    {activity.map((a) => (
                      <div key={a.id} className="flex gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#C6E76C] mt-2 flex-shrink-0"></div>
                        <div>
                          <p className="text-sm text-[#474747]">{a.description}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{a.created_at ? new Date(a.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="notes-tab-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#474747]">Notes {notes.length > 0 ? `(${notes.length})` : ''}</h3>
                  <Button
                    size="sm"
                    onClick={() => setNoteModalOpen(true)}
                    className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
                    data-testid="notes-tab-add-button"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Note
                  </Button>
                </div>
                {notes.length === 0 ? (
                  <p className="text-sm text-gray-500">No notes yet. Add the first one.</p>
                ) : (
                  <div className="space-y-3">
                    {notes.map((n) => (
                      <div key={n.id} className="bg-gray-50 rounded-lg p-3 border border-[#E9E9E9]">
                        <p className="text-sm text-[#474747] whitespace-pre-wrap">{n.body || n.note}</p>
                        <p className="text-xs text-gray-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : ''}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'tasks' && (
              <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="tasks-tab-panel">
                <h3 className="text-lg font-semibold text-[#474747] mb-4">Tasks {tabTasks.length > 0 ? `(${tabTasks.length})` : ''}</h3>
                {tabTasks.length === 0 ? (
                  <p className="text-sm text-gray-500">No tasks linked to this {isItem ? 'item' : 'application'}.</p>
                ) : (
                  <div className="divide-y divide-[#E9E9E9]">
                    {tabTasks.map((t) => (
                      <div key={t.id} className="py-3 flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${t.status === 'completed' ? 'text-gray-400 line-through' : 'text-[#474747]'}`}>{t.title}</p>
                          {t.description && <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>}
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${t.priority === 'high' ? 'bg-red-100 text-red-700' : t.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{t.priority}</span>
                            <span className="text-xs text-gray-400 capitalize">{t.status}</span>
                          </div>
                        </div>
                        {t.status !== 'completed' && (
                          <Button size="sm" variant="outline" className="border-[#E9E9E9]" onClick={() => completeTask(t.id)} data-testid={`complete-task-${t.id}`}>
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Complete
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Seller Card */}
            <div className="bg-white rounded-xl border border-[#E9E9E9] p-6">
              <h3 className="text-lg font-semibold text-[#474747] mb-4">Seller</h3>
              
              {seller && (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Name</p>
                    <p className="text-base text-[#474747] mt-1">{seller.full_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Phone</p>
                    <p className="text-base text-[#474747] mt-1">{seller.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Email</p>
                    <p className="text-base text-[#474747] mt-1">{seller.email || 'N/A'}</p>
                  </div>
                  
                  <Button
                    onClick={() => navigate(`/seller/${seller.id}`)}
                    className="w-full bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747]"
                  >
                    View Full Profile
                  </Button>
                </div>
              )}
            </div>

            {/* Price History (if item and sold) */}
            {isItem && data.sale_price && (
              <div className="bg-white rounded-xl border border-[#E9E9E9] p-6">
                <h3 className="text-lg font-semibold text-[#474747] mb-4">Price History</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Original</span>
                    <span className="text-sm font-medium text-[#474747]">{formatCurrency(data.nominated_sale_price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Listed</span>
                    <span className="text-sm font-medium text-[#474747]">{formatCurrency(data.listed_price)}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-[#E9E9E9]">
                    <span className="text-sm font-semibold text-[#474747]">Sold</span>
                    <span className="text-sm font-semibold text-[#474747]">{formatCurrency(data.sale_price)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Signed Sales Agreement */}
            {signedAgreement && (
              <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="signed-agreement-card">
                <h3 className="text-lg font-semibold text-[#474747] mb-1">Sales Agreement</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Signed by {signedAgreement.seller_full_name} on{' '}
                  {new Date(signedAgreement.signed_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
                <div className="space-y-2">
                  <Button
                    className="w-full justify-start bg-[#1E2117] hover:bg-[#33382A] text-[#C6E76C] font-semibold"
                    onClick={() => window.open(`${API}/public/agreements/${signedAgreement.id}`, '_blank')}
                    data-testid="view-signed-agreement-button"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View Signed Agreement
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={() => sendAgreement('sms')}
                      disabled={!!sendingAgreement}
                      data-testid="send-agreement-sms-button"
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      {sendingAgreement === 'sms' ? 'Sending…' : 'SMS'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => sendAgreement('email')}
                      disabled={!!sendingAgreement}
                      data-testid="send-agreement-email-button"
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      {sendingAgreement === 'email' ? 'Sending…' : 'Email'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Marketplace Listing ID */}
            {isItem && (data?.lifecycle_stage === 'live_on_marketplace' || data?.lifecycle_stage === 'sold' || data?.marketplace_id) && (
              <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="marketplace-id-card">
                <h3 className="text-lg font-semibold text-[#474747] mb-3">Marketplace Listing</h3>
                <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">Marketplace ID</p>
                <div className="flex gap-2">
                  <Input
                    value={marketplaceId}
                    onChange={(e) => setMarketplaceId(e.target.value)}
                    placeholder="e.g. 2078131859752986"
                    className="font-mono text-sm"
                    data-testid="marketplace-id-input"
                  />
                  <Button
                    onClick={saveMarketplaceId}
                    disabled={savingMarketplaceId || (marketplaceId.trim() === (data?.marketplace_id || ''))}
                    className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold shrink-0"
                    data-testid="save-marketplace-id-button"
                  >
                    {savingMarketplaceId ? 'Saving…' : 'Save'}
                  </Button>
                </div>
                {data?.marketplace_id && (
                  <button
                    onClick={() => copyText(data.marketplace_id, 'Marketplace ID')}
                    className="mt-2 flex items-center gap-1 text-xs text-gray-500 hover:text-[#474747]"
                    data-testid="copy-marketplace-id-button"
                  >
                    <Copy className="w-3 h-3" /> Copy ID
                  </button>
                )}
              </div>
            )}

            {/* Storage & QR Label */}
            {isItem && data?.qr_code && (
              <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="qr-label-card">
                <h3 className="text-lg font-semibold text-[#474747] mb-3">Storage &amp; QR Label</h3>
                <div className="flex items-center gap-4">
                  <img src={data.qr_code} alt="Item QR code" className="w-24 h-24 border border-[#E9E9E9] rounded" data-testid="item-qr-image" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Storage Space</p>
                    <p className="text-xl font-bold text-[#474747]" data-testid="item-storage-location">{data.storage_location || 'Not assigned'}</p>
                    <Button
                      size="sm"
                      onClick={printLabel}
                      className="mt-2 bg-[#1E2117] hover:bg-[#33382A] text-[#C6E76C] font-semibold"
                      data-testid="print-label-button"
                    >
                      Print Label
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-[#E9E9E9] p-6">
              <h3 className="text-lg font-semibold text-[#474747] mb-4">Quick Actions</h3>
              
              <div className="space-y-2">
                {!isItem && (data?.dropoff_datetime || data?.lifecycle_stage === 'awaiting_dropoff' || data?.lifecycle_stage === 'agreement_sent' || data?.agreement_signed) && (
                  <Button
                    className="w-full justify-start bg-[#1E2117] hover:bg-[#33382A] text-[#C6E76C] font-semibold"
                    data-testid="create-agreement-button"
                    onClick={() => setAgreementModalOpen(true)}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {data?.agreement_signed ? 'View Sales Agreement' : 'Create Sales Agreement'}
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="w-full justify-start border-[#E9E9E9]"
                  data-testid="quick-action-send-sms"
                  onClick={() => navigate('/messages', { state: { compose: { type: 'sms', to: seller?.phone || '' } } })}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send SMS
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start border-[#E9E9E9]"
                  data-testid="quick-action-send-email"
                  onClick={() => navigate('/messages', { state: { compose: { type: 'email', to: seller?.email || '' } } })}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start border-[#E9E9E9]"
                  data-testid="quick-action-schedule-booking"
                  onClick={openBookingModal}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Booking
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start border-[#E9E9E9]"
                  data-testid="quick-action-add-note"
                  onClick={() => setNoteModalOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stage Change Confirmation Modal */}
      <Dialog open={stageModalOpen} onOpenChange={setStageModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold text-[#474747]">
              Change Lifecycle Stage
            </DialogTitle>
          </DialogHeader>
          
          {targetStage && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Move this item to <span className="font-semibold">{targetStage.label}</span>?
              </p>
              
              {!isItem && targetStage.key === 'in_preparation' && (
                <div className="p-4 bg-purple-50 rounded-lg" data-testid="checkin-conversion-notice">
                  <p className="text-sm text-purple-800">
                    <span className="font-semibold">Item Check-in:</span> This converts the application into an
                    active item record (photos and details carry over) and starts the preparation checklist.
                  </p>
                </div>
              )}
              
              {isItem && targetStage.key === 'sold' && (
                <div className="space-y-3" data-testid="sold-form">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">Final Sale Price *</label>
                    <Input
                      type="number"
                      min="0"
                      value={saleForm.sale_price}
                      onChange={(e) => setSaleForm({ ...saleForm, sale_price: e.target.value })}
                      placeholder="0.00"
                      data-testid="sale-price-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">Buyer Name</label>
                    <Input
                      value={saleForm.buyer_name}
                      onChange={(e) => setSaleForm({ ...saleForm, buyer_name: e.target.value })}
                      placeholder="Optional"
                      data-testid="buyer-name-input"
                    />
                  </div>
                  {parseFloat(saleForm.sale_price) > 0 && (
                    <div className="p-3 bg-teal-50 rounded-lg text-sm text-teal-800" data-testid="payout-preview">
                      Seller receives <strong>${(parseFloat(saleForm.sale_price) * 0.7).toFixed(2)}</strong> (70%) ·
                      Listers commission <strong>${(parseFloat(saleForm.sale_price) * 0.3).toFixed(2)}</strong> (30%)
                    </div>
                  )}
                </div>
              )}
              
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Automated Actions:</span>
                  <br />
                  This will trigger automated SMS notifications and task creation per your lifecycle rules.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={confirmStageChange}
                  className="flex-1 bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
                >
                  Confirm
                </Button>
                <Button
                  onClick={() => setStageModalOpen(false)}
                  variant="outline"
                  className="border-[#E9E9E9]"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Booking Modal */}
      <Dialog open={bookingModalOpen} onOpenChange={setBookingModalOpen}>
        <DialogContent className="max-w-lg" data-testid="schedule-booking-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold text-[#474747]">Schedule Booking</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateBooking} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Type *</label>
              <select
                required
                value={bookingForm.type}
                onChange={(e) => handleBookingTypeChange(e.target.value)}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                data-testid="booking-type-select"
              >
                <option value="dropoff">Drop-off</option>
                <option value="inspection">Inspection</option>
                <option value="payout_reminder">Payout Reminder</option>
                <option value="internal_task">Internal Task</option>
                <option value="follow_up">Follow-up</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Title *</label>
              <input
                type="text"
                required
                value={bookingForm.title}
                onChange={(e) => setBookingForm({ ...bookingForm, title: e.target.value })}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                data-testid="booking-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#474747] block mb-2">Start Time *</label>
                <input
                  type="datetime-local"
                  required
                  value={bookingForm.start_time}
                  onChange={(e) => setBookingForm({ ...bookingForm, start_time: e.target.value })}
                  className="w-full p-2 border border-[#E9E9E9] rounded-md"
                  data-testid="booking-start-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#474747] block mb-2">End Time *</label>
                <input
                  type="datetime-local"
                  required
                  value={bookingForm.end_time}
                  onChange={(e) => setBookingForm({ ...bookingForm, end_time: e.target.value })}
                  className="w-full p-2 border border-[#E9E9E9] rounded-md"
                  data-testid="booking-end-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Location</label>
              <input
                type="text"
                value={bookingForm.location}
                onChange={(e) => setBookingForm({ ...bookingForm, location: e.target.value })}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                placeholder="e.g., Listers HQ"
                data-testid="booking-location-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Notes</label>
              <textarea
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                rows="3"
                data-testid="booking-notes-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={savingBooking}
                className="flex-1 bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
                data-testid="booking-submit-button"
              >
                {savingBooking ? 'Scheduling...' : 'Schedule Booking'}
              </Button>
              <Button type="button" onClick={() => setBookingModalOpen(false)} variant="outline" className="border-[#E9E9E9]">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Check-In Modal (assign storage + QR label) */}
      <Dialog open={checkinModalOpen} onOpenChange={setCheckinModalOpen}>
        <DialogContent className="max-w-md" data-testid="checkin-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold text-[#474747]">Check In Item</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Checking in converts this application to an item, generates its QR code label, and assigns it a storage space.
          </p>
          <form onSubmit={confirmCheckin} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Storage Space *</label>
              <input
                type="text"
                required
                value={checkinStorage}
                onChange={(e) => setCheckinStorage(e.target.value)}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                placeholder="e.g., Bay A3, Shelf 2"
                data-testid="checkin-storage-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={checkingIn}
                className="flex-1 bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
                data-testid="checkin-confirm-button"
              >
                {checkingIn ? 'Checking In...' : 'Check In & Generate QR Label'}
              </Button>
              <Button type="button" onClick={() => setCheckinModalOpen(false)} variant="outline" className="border-[#E9E9E9]">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Drop-off Scheduling Modal (Stage 3) */}
      <SalesAgreementModal
        open={agreementModalOpen}
        onOpenChange={setAgreementModalOpen}
        application={data}
        seller={seller}
        onSigned={loadData}
      />

      <Dialog open={dropoffModalOpen} onOpenChange={setDropoffModalOpen}>
        <DialogContent className="max-w-md" data-testid="dropoff-schedule-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold text-[#474747]">Book Drop-off Time</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Pick the drop-off time. It will be booked in the calendar and SMSed to the seller.
          </p>
          <form onSubmit={confirmDropoffSchedule} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Drop-off Date &amp; Time *</label>
              <input
                type="datetime-local"
                required
                value={dropoffForm.start_time}
                onChange={(e) => setDropoffForm({ ...dropoffForm, start_time: e.target.value })}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                data-testid="dropoff-datetime-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Location</label>
              <input
                type="text"
                value={dropoffForm.location}
                onChange={(e) => setDropoffForm({ ...dropoffForm, location: e.target.value })}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                data-testid="dropoff-location-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Notes</label>
              <textarea
                value={dropoffForm.notes}
                onChange={(e) => setDropoffForm({ ...dropoffForm, notes: e.target.value })}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                rows="2"
                data-testid="dropoff-notes-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={schedulingDropoff}
                className="flex-1 bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
                data-testid="dropoff-confirm-button"
              >
                {schedulingDropoff ? 'Booking...' : 'Book Drop-off & Notify Seller'}
              </Button>
              <Button type="button" onClick={() => setDropoffModalOpen(false)} variant="outline" className="border-[#E9E9E9]">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-md" data-testid="delete-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold text-[#474747]">Delete {isItem ? 'Item' : 'Application'}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This will permanently delete <span className="font-semibold">{data?.item_title || data?.title || 'this record'}</span> and all related data (bookings, tasks, messages{isItem ? ', payouts' : ''}). This cannot be undone.
          </p>
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
              data-testid="delete-confirm-button"
            >
              {deleting ? 'Deleting...' : 'Yes, Delete Permanently'}
            </Button>
            <Button onClick={() => setDeleteModalOpen(false)} variant="outline" className="border-[#E9E9E9]">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
        <DialogContent className="max-w-md" data-testid="add-note-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold text-[#474747]">Add Note</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddNote} className="space-y-4">
            <textarea
              required
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="w-full p-2 border border-[#E9E9E9] rounded-md"
              rows="4"
              placeholder="Write a note..."
              data-testid="note-text-input"
            />
            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={savingNote}
                className="flex-1 bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
                data-testid="note-submit-button"
              >
                {savingNote ? 'Saving...' : 'Add Note'}
              </Button>
              <Button type="button" onClick={() => setNoteModalOpen(false)} variant="outline" className="border-[#E9E9E9]">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
